<template>
    <div class="page-wrap">
        <div class="page-bar">
            <div>
                <div class="page-bar-sub">Settings</div>
                <div class="page-bar-main">Folders</div>
            </div>
        </div>
        <div :class="$store.state.routeLoaded ? 'slide-up view' : 'hide'">
            <div class="folders-head">
                <div class="orgteam-bar" style="margin:0">
                    <button type="button" class="orgteam-btn" :class="{ on: view == 'list' }" @click="setView('list')">List</button>
                    <button type="button" class="orgteam-btn" :class="{ on: view == 'tree' }" @click="setView('tree')">Tree</button>
                </div>
                <div v-if="canEditNodes" class="add-btn" @click="addNode('home', 'NODE')">Add Folder to Home</div>
            </div>
            <p class="folders-desc" v-if="view == 'list'">Every folder and process belong to one or more departments, which determines who sees it. A person assigned to one of those departments will see those items.</p>
            <p class="folders-desc" v-else>The whole structure at a glance: every folder, subfolder, and the contents inside.</p>

            <table v-if="view == 'list'" class="full-table folders-table">
                <tbody>
                    <tr class="table-labels">
                        <td style="padding-left:42px">Name</td>
                        <td>Department</td>
                        <td>Contains</td>
                        <td style="padding:0"></td>
                    </tr>
                    <tr style="padding:0;height:0"></tr>
                    <template v-for="row in listRows" :key="row.key">
                        <tr v-if="row.kind == 'section'" class="folders-section-row">
                            <td colspan="4">{{ row.label }}</td>
                        </tr>
                        <tr v-else-if="row.kind == 'add'" class="folder-add-row">
                            <td colspan="4" :style="indentStyle(row.depth)">
                                <a @click.prevent="addNode(row.node.Slug, 'NODE')">Add subfolder</a>
                                <a @click.prevent="addNode(row.node.Slug)">Add item</a>
                                <a @click.prevent="addExisting(row.node)">Add existing to {{ row.node.Name }}</a>
                            </td>
                        </tr>
                        <tr v-else :class="row.depth > 0 || row.node.Type != 'NODE' ? 'folder-row-sub' : ''">
                            <td :style="indentStyle(row.depth)">
                                <div class="folder-name">
                                    <button v-if="row.canExpand" type="button" class="folder-chev" :class="{ open: row.expanded }" @click="toggle(row.key)">
                                        <svg-icon icon="chevright" />
                                    </button>
                                    <span v-else class="folder-chev-spacer"></span>
                                    <span class="folder-icon"><svg-icon :icon="typeIcon(row.node.Type)" /></span>
                                    <a v-if="row.node.Type == 'NODE'" @click.prevent="row.canExpand ? toggle(row.key) : null">{{ row.node.Name }}</a>
                                    <a v-else-if="row.node.Type == 'LINK'" :href="row.node.Link" target="_blank">{{ row.node.Name }}</a>
                                    <router-link v-else :to="'/playbook/' + row.node.Slug">{{ row.node.Name }}</router-link>
                                    <span class="folder-tag" :class="{ 'is-folder': row.node.Type == 'NODE' }">{{ typeLabel(row.node.Type, row.depth) }}</span>
                                    <span v-if="row.node.NeedsApproval" class="folder-tag is-status">{{ row.node.NeedsApprovalType || 'Needs Approval' }}</span>
                                    <span v-if="row.isLoop" class="folder-tag" title="This folder links back to one of the folders above it">Loop</span>
                                </div>
                            </td>
                            <td>
                                <template v-if="row.node.Type == 'NODE' && row.depth == 0">
                                    <span v-for="dep in departments(row.node)" :key="dep" class="pill p-live folder-dept-pill">{{ dep }}</span>
                                </template>
                                <span v-else class="folder-muted">{{ departments(row.node).join(', ') }}</span>
                            </td>
                            <td>
                                <span v-if="row.node.Type == 'NODE'" :class="row.depth > 0 ? 'folder-muted' : ''">{{ containsLabel(row.node.Slug) }}</span>
                                <span v-else class="folder-muted">&mdash;</span>
                            </td>
                            <td v-if="canEditNodes" style="text-align:center">
                                <div class="row-menu">
                                    <button type="button" class="row-menu-btn" @click.stop="toggleRowMenu(row.key)">
                                        <svg-icon icon="dots" />
                                    </button>
                                    <div v-if="openMenuKey == row.key" class="row-menu-dropdown">
                                        <router-link v-if="row.node.Type != 'LINK'" :to="'/playbook/' + row.node.Slug" @click="closeRowMenu">Open</router-link>
                                        <a @click.prevent="rowMenuAction(() => editNode(row.node))">Edit Details</a>
                                        <router-link v-if="row.node.Type == 'PROCESS'" :to="'/edit-process/' + row.node.Slug" @click="closeRowMenu">Edit Process</router-link>
                                        <router-link v-if="row.node.Type == 'TIMELINE'" :to="'/edit-timeline/' + row.node.Slug" @click="closeRowMenu">Edit Value Engine</router-link>
                                        <router-link v-if="row.node.Type == 'SHEET'" :to="'/edit-sheet/' + row.node.Slug" @click="closeRowMenu">Edit Sheet</router-link>
                                        <router-link v-if="row.node.Type == 'CHECKLIST'" :to="'/edit-checklist/' + row.node.Slug" @click="closeRowMenu">Edit Checklist</router-link>
                                        <template v-if="row.node.Type == 'NODE'">
                                            <a @click.prevent="rowMenuAction(() => addNode(row.node.Slug, 'NODE'))">Add subfolder</a>
                                            <a @click.prevent="rowMenuAction(() => addNode(row.node.Slug))">Add item</a>
                                            <a @click.prevent="rowMenuAction(() => addExisting(row.node))">Add existing</a>
                                        </template>
                                        <a @click.prevent="rowMenuAction(() => addToFolder(row.node))">Add to Folder</a>
                                        <div class="row-menu-divider"></div>
                                        <a v-if="row.parentSlug" @click.prevent="rowMenuAction(() => removeFromParent(row))">Remove from {{ row.parentName }}</a>
                                        <a class="row-menu-delete" @click.prevent="rowMenuAction(() => deleteNode(row.node))">Delete</a>
                                    </div>
                                </div>
                            </td>
                            <td v-else style="padding:0"></td>
                        </tr>
                    </template>
                    <tr v-if="!listRows.length">
                        <td colspan="4" class="folder-muted">No folders yet.</td>
                    </tr>
                </tbody>
            </table>

            <div v-else class="folder-tree">
                <template v-for="row in treeRows" :key="row.key">
                    <div v-if="row.kind == 'section'" class="folder-tree-section">{{ row.label }}</div>
                    <div v-else class="folder-tree-row" :class="{ 'is-nested': row.depth > 0 }" :style="{ marginLeft: (row.depth * 26) + 'px' }">
                        <span class="folder-icon"><svg-icon :icon="typeIcon(row.node.Type)" /></span>
                        <a v-if="row.node.Type == 'LINK'" :href="row.node.Link" target="_blank">{{ row.node.Name }}</a>
                        <router-link v-else :to="'/playbook/' + row.node.Slug" :class="{ 'is-folder': row.node.Type == 'NODE' }">{{ row.node.Name }}</router-link>
                        <span class="folder-tag" :class="{ 'is-folder': row.node.Type == 'NODE' }">{{ typeLabel(row.node.Type, row.depth) }}</span>
                        <span v-if="row.node.Type == 'NODE' && row.depth == 0" class="folder-tag">{{ departments(row.node).join(', ') }}</span>
                        <span v-if="row.node.NeedsApproval" class="folder-tag is-status">{{ row.node.NeedsApprovalType || 'Needs Approval' }}</span>
                        <span v-if="row.isLoop" class="folder-tag" title="This folder links back to one of the folders above it">Loop</span>
                    </div>
                </template>
                <div v-if="!treeRows.length" class="folder-muted">No folders yet.</div>
            </div>
        </div>

        <div v-if="showConfirmDeleteOrDraft" class="error-msg-wrap">
            <div class="error-msg">
                <div>Would you like to remove this item?</div>
                <div style="margin-bottom:10px"><div @click="unapproveItem('Draft')" class="btn">Yes, Move to Draft</div></div>
                <div style="margin-bottom:10px"><div @click="unapproveItem('Needs Approval')" class="btn">Yes, Move to Needs Approval</div></div>
                <div style="margin-bottom:10px"><div @click="unapproveItem('Inactive')" class="btn">Yes, Move to Inactive</div></div>
                <div><div @click="() => { showConfirmDeleteOrDraft = false; }" class="btn-invert">Cancel</div></div>
            </div>
        </div>

        <add-edit-node v-if="showAddNode" :type="addType" :fromSlug="addFromSlug" @close="closeModals()" />
        <add-edit-node v-if="showEditNode" :node="nodeToEdit" @close="closeModals()" />
        <add-existing-node v-if="showAddExisting" :fromSlug="addFromSlug" @close="closeModals()" />
        <add-node-to-folder v-if="showAddToFolder" :toSlug="slugToAddToFolder" @close="closeModals()" />
        <confirm-delete v-if="showConfirmDelete" @confirm="deleteNodeConfirm" @cancel="() => { showConfirmDelete = false; }" msg="Are you sure you would like to PERMANENTLY DELETE this item?"/>
        <confirm-delete v-if="showConfirmRemove" @confirm="removeFromParentConfirm" @cancel="() => { showConfirmRemove = false; }" :msg="'Are you sure you would like to remove ' + (rowToRemove?.node.Name || 'this item') + ' from ' + (rowToRemove?.parentName || '') + '?'"/>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';
import AddEditNode from '@/components/AddEditNode.vue';
import AddExistingNode from '@/components/AddExistingNode.vue';
import AddNodeToFolder from '@/components/AddNodeToFolder.vue';
import ConfirmDelete from '@/components/ConfirmDelete.vue';
import SvgIcon from '@/components/SvgIcon.vue';

const TYPE_LABELS: any = {
    NODE: 'Folder', PROCESS: 'Process', TIMELINE: 'Value Engine', SHEET: 'Role Sheet',
    CHECKLIST: 'Checklist', VIDEO: 'Video', LINK: 'Link'
};
const TYPE_ICONS: any = {
    NODE: 'folder', PROCESS: 'list', TIMELINE: 'timeline', SHEET: 'table',
    CHECKLIST: 'checklist', VIDEO: 'play', LINK: 'link'
};

export default defineComponent({
    name: 'FoldersView',
    components: {
        AddEditNode, AddExistingNode, AddNodeToFolder, ConfirmDelete, SvgIcon
    },
    data() {
        return {
            view: 'list',
            nodeMap: {} as any,
            childMap: {} as any,
            expanded: {} as any,
            openMenuKey: null as any,
            showAddNode: false,
            addType: null as any,
            addFromSlug: null as any,
            showEditNode: false,
            nodeToEdit: null as any,
            showAddExisting: false,
            showAddToFolder: false,
            slugToAddToFolder: null as any,
            nodeToDelete: null as any,
            showConfirmDelete: false,
            showConfirmDeleteOrDraft: false,
            rowToRemove: null as any,
            showConfirmRemove: false
        }
    },
    computed: {
        canEditNodes(): boolean {
            return this.$store.state.adminRolesCanEdit.includes(this.$store.state.user.role);
        },
        // Folders nothing links to (not reachable from home) — shown in their own section so they aren't lost
        orphanFolders(): any[] {
            const linked = new Set<string>();
            for(const parent of Object.keys(this.childMap)) {
                for(const child of this.childMap[parent]) {
                    linked.add(child.Slug);
                }
            }
            return Object.values(this.nodeMap).filter((n: any) => {
                return n.Type == 'NODE' && !linked.has(n.Slug);
            });
        },
        listRows(): any[] {
            return this.buildRows(false);
        },
        treeRows(): any[] {
            return this.buildRows(true);
        }
    },
    async created() {
        try {
            const savedView = localStorage.getItem('foldersView');
            if(savedView == 'list' || savedView == 'tree') {
                this.view = savedView;
            }
        } catch(e) { /* storage unavailable */ }

        await this.getTree();

        this.$store.state.routeLoaded = true;
    },
    methods: {
        async getTree() {
            await axios.get(`${this.$store.state.apiUrl}/api/playbook/foldertree`)
                .then(resp => {
                    const nodeMap: any = {};
                    for(const node of resp.data.nodes) {
                        nodeMap[node.Slug] = node;
                    }

                    // Same ordering as /nextnodes: link Order (nulls last), then name
                    const childMap: any = {};
                    const links = resp.data.links.filter((l: any) => nodeMap[l.NextSlug]);
                    links.sort((a: any, b: any) => {
                        const aOrder = a.Order == null ? Infinity : a.Order;
                        const bOrder = b.Order == null ? Infinity : b.Order;
                        if(aOrder != bOrder) {
                            return aOrder - bOrder;
                        }
                        return nodeMap[a.NextSlug].Name.localeCompare(nodeMap[b.NextSlug].Name);
                    });
                    for(const link of links) {
                        if(link.ThisSlug != 'home' && !nodeMap[link.ThisSlug]) {
                            continue;
                        }
                        if(!childMap[link.ThisSlug]) {
                            childMap[link.ThisSlug] = [];
                        }
                        childMap[link.ThisSlug].push(nodeMap[link.NextSlug]);
                    }

                    this.nodeMap = nodeMap;
                    this.childMap = childMap;
                })
                .catch(e => {
                    console.log(e?.response?.data || e);
                    alert('There was an error loading folders');
                })
        },
        // Flattens the graph into table rows. A node can appear under several parents, so rows are keyed
        // by their full path; a folder already in its own path is a loop and isn't expanded again.
        buildRows(expandAll: boolean): any[] {
            const rows: any[] = [];
            const walk = (parentSlug: string | null, parentName: string | null, children: any[], depth: number, path: string[]) => {
                for(const node of children) {
                    const key = [...path, node.Slug].join('/');
                    const isLoop = path.includes(node.Slug);
                    const canExpand = node.Type == 'NODE' && !isLoop;
                    const expanded = canExpand && (expandAll || !!this.expanded[key]);
                    rows.push({ kind: 'node', key, node, depth, parentSlug, parentName, isLoop, canExpand, expanded });
                    if(expanded) {
                        walk(node.Slug, node.Name, this.childMap[node.Slug] || [], depth + 1, [...path, node.Slug]);
                        if(!expandAll && this.canEditNodes) {
                            rows.push({ kind: 'add', key: key + '#add', node, depth: depth + 1 });
                        }
                    }
                }
            };

            walk('home', 'Home', this.childMap['home'] || [], 0, ['home']);
            if(this.orphanFolders.length) {
                rows.push({ kind: 'section', key: '#orphans', label: 'Not in any folder' });
                walk(null, null, this.orphanFolders, 0, ['#orphans']);
            }
            return rows;
        },
        setView(view: string) {
            this.view = view;
            this.closeRowMenu();
            try {
                localStorage.setItem('foldersView', view);
            } catch(e) { /* storage unavailable */ }
        },
        toggle(key: string) {
            this.expanded[key] = !this.expanded[key];
        },
        indentStyle(depth: number) {
            return { paddingLeft: (14 + depth * 28) + 'px' };
        },
        typeIcon(type: string): string {
            return TYPE_ICONS[type] || 'lines';
        },
        typeLabel(type: string, depth: number): string {
            if(type == 'NODE' && depth > 0) {
                return 'Subfolder';
            }
            return TYPE_LABELS[type] || type;
        },
        departments(node: any): string[] {
            if(!node.DepartmentsJSON) {
                return [];
            }
            try {
                return JSON.parse(node.DepartmentsJSON);
            } catch(e) {
                return [];
            }
        },
        containsLabel(slug: string): string {
            const children = this.childMap[slug] || [];
            if(!children.length) {
                return 'Empty';
            }
            const folders = children.filter((c: any) => c.Type == 'NODE').length;
            const items = children.length - folders;
            const parts = [];
            if(folders) {
                parts.push(`${folders} folder${folders == 1 ? '' : 's'}`);
            }
            if(items) {
                parts.push(`${items} item${items == 1 ? '' : 's'}`);
            }
            return parts.join(' · ');
        },
        addNode(fromSlug: string, type?: string) {
            this.addFromSlug = fromSlug;
            this.addType = type || null;
            this.showAddNode = true;
        },
        addExisting(node: any) {
            this.addFromSlug = node.Slug;
            this.showAddExisting = true;
        },
        addToFolder(node: any) {
            this.slugToAddToFolder = node.Slug;
            this.showAddToFolder = true;
        },
        // The tree only carries light columns; saving a partial node would blank its content, so load it in full
        async editNode(node: any) {
            await axios.get(`${this.$store.state.apiUrl}/api/playbook/node/${node.Slug}`)
                .then(resp => {
                    this.nodeToEdit = resp.data;
                    this.showEditNode = true;
                })
                .catch(e => {
                    console.log(e?.response?.data || e);
                    alert('There was an error');
                })
        },
        async closeModals() {
            // Expand the folder something was just added to so the new item is visible
            if((this.showAddNode || this.showAddExisting) && this.addFromSlug && this.addFromSlug != 'home') {
                for(const row of this.listRows) {
                    if(row.kind == 'node' && row.node.Slug == this.addFromSlug && row.canExpand) {
                        this.expanded[row.key] = true;
                    }
                }
            }
            this.showAddNode = false;
            this.showEditNode = false;
            this.showAddExisting = false;
            this.showAddToFolder = false;
            await this.getTree();
        },
        removeFromParent(row: any) {
            this.rowToRemove = row;
            this.showConfirmRemove = true;
        },
        async removeFromParentConfirm() {
            await axios.post(`${this.$store.state.apiUrl}/api/playbook/removenode`, { fromSlug: this.rowToRemove.parentSlug, Slug: this.rowToRemove.node.Slug })
                .catch(e => {
                    console.log(e?.response?.data || e);
                    alert('There was an error');
                })

            await this.getTree();

            this.showConfirmRemove = false;
        },
        // Same rules as Nodes.vue: content items move to Draft/Needs Approval/Inactive instead of deleting,
        // folders can only be deleted once nothing links to them, links/videos confirm a permanent delete
        async deleteNode(node: any) {
            this.nodeToDelete = node;
            if(node.Type == 'PROCESS' || node.Type == 'TIMELINE' || node.Type == 'SHEET' || node.Type == 'CHECKLIST') {
                this.showConfirmDeleteOrDraft = true;
            } else if(node.Type == 'NODE') {
                let routes: any = null;
                await axios.get(`${this.$store.state.apiUrl}/api/playbook/paths?slug=${node.Slug}`)
                    .then(resp => {
                        routes = resp.data;
                    })
                    .catch(e => {
                        alert('There was an error')
                        console.log(e?.response?.data || e);
                    })
                if(!routes) {
                    return;
                }
                if(routes.length) {
                    alert('This node is linked to from other nodes. Remove this node from all routes before you can delete.');
                    return;
                } else {
                    this.showConfirmDelete = true;
                }
            } else {
                this.showConfirmDelete = true;
            }
        },
        async deleteNodeConfirm() {
            await axios.post(`${this.$store.state.apiUrl}/api/playbook/deletenode`, this.nodeToDelete)
                .catch(e => {
                    console.log(e?.response?.data || e);
                    alert('There was an error')
                })

            await this.getTree();

            this.showConfirmDelete = false;
        },
        unapproveItem(type: string) {
            axios.post(`${this.$store.state.apiUrl}/api/playbook/node/unapprove/${this.nodeToDelete.Slug}`, { type: type })
                .then(resp => {
                    this.getTree();
                    this.$store.dispatch('setStoreData');
                    alert(this.nodeToDelete.Name + ' has been moved to ' + type);
                    this.showConfirmDeleteOrDraft = false;
                })
                .catch(e => {
                    console.log(e?.response?.data || e);
                    alert('There was an error')
                })
        },
        toggleRowMenu(key: string) {
            this.openMenuKey = this.openMenuKey == key ? null : key;
        },
        closeRowMenu() {
            this.openMenuKey = null;
        },
        rowMenuAction(action: any) {
            this.closeRowMenu();
            action();
        },
        handleOutsideRowMenuClick(e: MouseEvent) {
            if(this.openMenuKey && !(e.target as HTMLElement).closest('.row-menu')) {
                this.openMenuKey = null;
            }
        }
    },
    mounted() {
        document.addEventListener('click', this.handleOutsideRowMenuClick);
    },
    beforeUnmount() {
        document.removeEventListener('click', this.handleOutsideRowMenuClick);
    }
});
</script>
